import { useMemo, useState, useEffect } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown, X } from "lucide-react";
import { Button, Checkbox } from "@carry/ui";
import { Link, createRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type ColumnSizingState,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";
import { useDebouncer } from "@tanstack/react-pacer";
import type { BulkConfirmOrdersOutputDto, OrderDto } from "@carry/contract";
import {
  BACKFILL_PARTIAL_TIME_WINDOWS_MAX,
  BACKFILL_PARTIAL_TIME_WINDOWS_SCAN_MAX,
  BULK_CONFIRM_ORDERS_MAX,
} from "@carry/contract";
import {
  ORDER_STATUS_LABELS,
  canBackfillOrderTimeWindow,
  classifyDeliveryTimeSlot,
  collectMissingConfirmationFields,
  isOrderContentLocked,
  orderHasPartialTimeWindow,
  timeSlotShortLabel,
  type TimeSlot,
} from "@carry/shared";
import { Route as RootRoute } from "./__root";
import { WeekStrip } from "../components/week-strip";
import { useAuth } from "../providers/auth-context";
import { useAdminApi } from "../providers/api-context";
import { useTimeSlotBoundaries } from "../lib/use-time-slot-boundaries";
import { useImportTimeTolerance } from "../lib/use-import-time-tolerance";
import { TablePagination } from "../components/table-pagination";
import { SearchInput } from "../components/search-input";
import { TableSkeletonRows } from "../components/table-skeleton";
import { QueryErrorBanner } from "../components/query-error-banner";
import { localToday, shiftDate, shiftMonth } from "../lib/format";
import { BulkImportModal } from "../components/order-form/bulk-import-modal";
import { confirm } from "../components/confirm-dialog";
import { validateOrderListSearch, type OrderListStatus } from "./order-list-search";

type StatusTab = OrderListStatus;

const STATUS_TABS: { v: StatusTab; label: string }[] = [
  { v: "all", label: "すべて" },
  { v: "draft", label: ORDER_STATUS_LABELS.draft },
  { v: "confirmed", label: ORDER_STATUS_LABELS.confirmed },
  { v: "assigned", label: ORDER_STATUS_LABELS.assigned },
  { v: "completed", label: ORDER_STATUS_LABELS.completed },
  { v: "cancelled", label: ORDER_STATUS_LABELS.cancelled },
];

// 色は画面固有、ラベルは shared の ORDER_STATUS_LABELS (単一ソース) を使う。
const STATUS_CHIP_COLOR: Record<OrderDto["status"], { bg: string; ink: string }> = {
  draft: { bg: "var(--st-draft-bg)", ink: "var(--st-draft-ink)" },
  confirmed: { bg: "var(--st-confirmed-bg)", ink: "var(--st-confirmed-ink)" },
  assigned: { bg: "var(--st-assigned-bg)", ink: "var(--st-assigned-ink)" },
  completed: { bg: "var(--st-done-bg)", ink: "var(--st-done-ink)" },
  cancelled: { bg: "var(--st-cancel-bg)", ink: "var(--st-cancel-ink)" },
};

const TIME_SLOT_CHIP: Record<TimeSlot, { bg: string; ink: string }> = {
  early_morning: { bg: "#e3ebe6", ink: "#1e4a3a" },
  am: { bg: "#ecebe1", ink: "#4a4423" },
  pm: { bg: "#e0e5ee", ink: "#1e2b55" },
};

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/orders",
  validateSearch: validateOrderListSearch,
  beforeLoad: ({ context }) => {
    if (context.authLoading) return;
    if (!context.session) throw redirect({ to: "/auth/login" });
  },
  component: OrdersPage,
});

function OrdersPage() {
  const { session } = useAuth();
  const api = useAdminApi();
  const navigate = useNavigate({ from: "/orders" });
  const orderListSearch = Route.useSearch();
  const { q, page, pageSize, status, delivery_date, sort } = orderListSearch;
  const today = useMemo(() => localToday(), []);

  const weekDays = useMemo(() => {
    const baseDow = new Date(`${delivery_date}T00:00:00`).getDay(); // 0=日 … 6=土
    return [0, 1, 2, 3, 4, 5, 6].map((o) => {
      const d = shiftDate(delivery_date, o - baseDow); // 日曜起点
      const dt = new Date(`${d}T00:00:00`);
      return {
        d,
        dow: ["日", "月", "火", "水", "木", "金", "土"][dt.getDay()] ?? "",
        dom: dt.getDate(),
        isToday: d === today,
      };
    });
  }, [delivery_date, today]);

  const monthLabel = useMemo(() => {
    const first = weekDays[0];
    const last = weekDays[6];
    if (!first || !last) return "";
    const [fy, fm] = first.d.split("-");
    const lm = last.d.split("-")[1];
    const fmNum = Number(fm);
    const lmNum = Number(lm);
    return fmNum === lmNum ? `${fy}年${fmNum}月` : `${fy}年${fmNum}月〜${lmNum}月`;
  }, [weekDays]);

  const [localQ, setLocalQ] = useState(q);
  // #689: 一括確定の選択状態。行が消える (日付・タブ・検索の変更) と TanStack Table 側の
  // 選択行モデルからも外れるが、条件を戻したときに古い選択が復活しないよう明示的に捨てる。
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [bulkResult, setBulkResult] = useState<BulkConfirmOrdersOutputDto["data"] | null>(null);
  const clearSelection = () => {
    setRowSelection({});
    setBulkResult(null);
  };

  useEffect(() => {
    setLocalQ(q);
  }, [q]);

  const debouncer = useDebouncer(
    (value: string) => navigate({ search: (prev) => ({ ...prev, q: value, page: 1 }) }),
    { wait: 300 },
    (state) => ({ isPending: state.isPending }),
  );

  const handleQChange = (value: string) => {
    setLocalQ(value);
    // 検索条件が変われば選択対象の行も変わる。意図しない受注を確定しないよう選択は捨てる。
    clearSelection();
    debouncer.maybeExecute(value);
  };

  const sorting = useMemo<SortingState>(() => parseSortParam(sort), [sort]);
  const handleSortingChange = (updater: SortingState | ((s: SortingState) => SortingState)) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    navigate({ search: (prev) => ({ ...prev, sort: stringifySortParam(next) }) });
  };

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() => loadColumnSizing());
  const [importOpen, setImportOpen] = useState(false);
  useEffect(() => {
    if (Object.keys(columnSizing).length > 0) {
      try {
        window.localStorage.setItem(COL_SIZE_STORAGE_KEY, JSON.stringify(columnSizing));
      } catch {
        // ignore quota errors
      }
    }
  }, [columnSizing]);

  const handleTabChange = (tab: StatusTab) => {
    clearSelection();
    navigate({ search: (prev) => ({ ...prev, status: tab, page: 1 }) });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["orders", session?.user?.id, status, delivery_date],
    queryFn: async () => {
      // #754: 取得件数は一括補完 API の走査上限と同じ定数を使う。ここだけ増やすと画面が
      // 数えた集合と API が見る集合がズレて、「N 件あります」と出しているのに別の件数が更新される。
      const input: {
        limit: number;
        status?: OrderDto["status"];
        delivery_date?: string;
        include_items: boolean;
      } = { limit: BACKFILL_PARTIAL_TIME_WINDOWS_SCAN_MAX, delivery_date, include_items: true };
      if (status !== "all") input.status = status;
      return await api.order.list(input);
    },
    enabled: !!session,
    // メール/取込で入った新規受注を運用者が手動リロードせず把握できるよう自動更新する.
    refetchInterval: 45_000,
    refetchOnWindowFocus: true,
  });

  // #641: 境界の解決は use-time-slot-boundaries に一本化する (画面ごとの写経を防ぐ)。
  // #641: 境界を取得できるまで便区分は描かない (初期値で一瞬別の便を出さない)。
  // 取得に失敗したときはスケルトンで固めず、理由と再試行を出して一覧自体は表示する。
  const {
    boundaries: slotBoundaries,
    isLoading: boundariesLoading,
    error: boundariesError,
    refetch: refetchBoundaries,
  } = useTimeSlotBoundaries();

  const filtered = useMemo<OrderDto[]>(() => {
    if (!data) return [];
    if (!q) return data.data;
    const lower = q.toLowerCase();
    return data.data.filter((o) =>
      [o.order_number, o.delivery_address, o.client_name, o.store_name]
        .filter((v): v is string => !!v)
        .some((v) => v.toLowerCase().includes(lower)),
    );
  }, [data, q]);

  const counts = useMemo(() => {
    const base: Record<StatusTab, number> = {
      all: 0,
      draft: 0,
      confirmed: 0,
      assigned: 0,
      completed: 0,
      cancelled: 0,
    };
    if (!data) return base;
    base.all = data.pagination.total;
    for (const o of data.data) base[o.status]++;
    return base;
  }, [data]);

  const columns: ColumnDef<OrderDto>[] = [
    // #689: 一括確定の選択列。確定できるのは下書きのみなので、他ステータスは選べない。
    {
      id: "select",
      size: 36,
      enableSorting: false,
      enableResizing: false,
      header: ({ table }) => {
        const selectable = table.getPrePaginationRowModel().rows.filter((r) => r.getCanSelect());
        return (
          <Checkbox
            aria-label="表示中の下書きをすべて選択"
            disabled={selectable.length === 0}
            checked={selectable.length > 0 && selectable.every((r) => r.getIsSelected())}
            onCheckedChange={(next: boolean) => {
              setBulkResult(null);
              for (const row of selectable) row.toggleSelected(next === true);
            }}
          />
        );
      },
      cell: ({ row }) => {
        // 必須項目が欠けている下書きは選べない (下の enableRowSelection)。理由が分からないと
        // 「なぜ選べないのか」で止まるので、不足項目を tooltip で示す。
        const missing = collectMissingConfirmationFields(row.original);
        return (
          <Checkbox
            aria-label={`注文 ${row.original.order_number ?? row.original.id} を選択`}
            title={missing.length > 0 ? `不足項目: ${missing.join(" / ")}` : undefined}
            disabled={!row.getCanSelect()}
            checked={row.getIsSelected()}
            onCheckedChange={(next: boolean) => {
              setBulkResult(null);
              row.toggleSelected(next === true);
            }}
          />
        );
      },
    },
    {
      accessorKey: "order_number",
      header: "注文番号",
      size: 130,
      cell: ({ getValue }) => (
        <span style={{ fontSize: 12 }}>{getValue<string | null>() ?? ""}</span>
      ),
    },
    {
      accessorKey: "delivery_date",
      header: "配送日",
      size: 110,
      cell: ({ getValue }) => (
        <span className="num" style={{ fontSize: 12 }}>
          {formatDeliveryDate(getValue<string>())}
        </span>
      ),
    },
    {
      id: "time_slot",
      header: "時間帯",
      size: 80,
      enableSorting: false,
      // #659: 列の値も表示と同じ導出 (配達時刻が範囲指定なら終了時刻で判定) にする。
      // 開始時刻を値に残すと、フィルタやソートを有効にした瞬間に表示と食い違う。
      accessorFn: (o) =>
        slotBoundaries
          ? classifyDeliveryTimeSlot(
              { start: o.delivery_time_start, end: o.delivery_time_end },
              slotBoundaries,
            )
          : null,
      cell: ({ getValue }) => {
        // 境界の取得前は行自体をスケルトンにしているため、ここへは来ない。
        // 境界を取得できなかった場合は便を推測せず空欄にする (バナーで理由を出す)。
        const slot = getValue<TimeSlot | null>();
        if (!slot) return <span style={{ fontSize: 11, color: "var(--ink-4)" }}></span>;
        const chip = TIME_SLOT_CHIP[slot];
        return (
          <span
            style={{
              fontSize: 10,
              padding: "1px 8px",
              borderRadius: 3,
              background: chip.bg,
              color: chip.ink,
              whiteSpace: "nowrap",
            }}
          >
            {timeSlotShortLabel(slot)}
          </span>
        );
      },
    },
    {
      accessorKey: "client_name",
      header: "取引先",
      size: 140,
      cell: ({ getValue }) => (
        <span
          style={{
            fontSize: 11,
            color: "var(--ink-2)",
            maxWidth: 160,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }}
        >
          {getValue<string | null>() ?? ""}
        </span>
      ),
    },
    {
      accessorKey: "store_name",
      header: "集荷店舗",
      size: 140,
      cell: ({ getValue }) => (
        <span
          style={{
            fontSize: 11,
            color: "var(--ink-2)",
            maxWidth: 160,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }}
        >
          {getValue<string | null>() ?? ""}
        </span>
      ),
    },
    {
      accessorKey: "delivery_address",
      header: "配送先",
      cell: ({ getValue, row }) => (
        <div
          style={{
            maxWidth: 280,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 3,
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: "var(--ink-2)",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {getValue<string>()}
          </span>
          {!row.original.delivery_coordinates_resolved && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "oklch(0.48 0.2 35)",
                background: "oklch(0.97 0.04 35)",
                border: "0.5px solid oklch(0.78 0.12 35)",
                borderRadius: 3,
                padding: "1px 5px",
              }}
            >
              座標未設定
            </span>
          )}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "ステータス",
      size: 100,
      cell: ({ getValue, row }) => {
        const status = getValue<OrderDto["status"]>();
        const missing = status === "draft" ? collectMissingConfirmationFields(row.original) : [];
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <StatusChip status={status} />
            {missing.length > 0 && (
              <span
                title={`不足項目: ${missing.join(" / ")}`}
                style={{ fontSize: 9, fontWeight: 700, color: "var(--danger)" }}
              >
                要入力
              </span>
            )}
          </div>
        );
      },
    },
  ];

  // #641: 便区分セルは境界が要る。読込中はスケルトンだけを出し、実データ行と重ねない。
  // 取得失敗時は行を出す (便欄は空欄) + バナーで理由を出す。永久スケルトンにしない。
  const showSkeleton = isLoading || boundariesLoading;
  const table = useReactTable({
    data: filtered,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // 選択キーを行 index ではなく public_id にし、並び替え・ページ移動で選択がズレないようにする。
    getRowId: (row) => row.id,
    // #689: 一括確定は draft → confirmed のみ。確定済み・配車済みなどは選択自体させない。
    enableRowSelection: (row) =>
      row.original.status === "draft" &&
      collectMissingConfirmationFields(row.original).length === 0,
    state: { pagination: { pageIndex: page - 1, pageSize }, sorting, columnSizing, rowSelection },
    onRowSelectionChange: setRowSelection,
    onSortingChange: handleSortingChange,
    onColumnSizingChange: setColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    defaultColumn: { minSize: 60, maxSize: 600 },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === "function" ? updater({ pageIndex: page - 1, pageSize }) : updater;
      navigate({
        search: (prev) => ({ ...prev, page: next.pageIndex + 1, pageSize: next.pageSize }),
      });
    },
    manualPagination: false,
  });

  // #689: 選択した受注をまとめて確定する。
  // サーバは全件ロールバックせず「確定できた分をコミットし、失敗は理由付きで返す」ため、
  // 成功分は選択から外し、失敗分は理由を直して再実行できるよう選択に残す。
  const queryClient = useQueryClient();
  const selectedIds = table.getSelectedRowModel().rows.map((row) => row.original.id);
  const overLimit = selectedIds.length > BULK_CONFIRM_ORDERS_MAX;
  const bulkConfirm = useMutation({
    mutationFn: async (publicIds: string[]) =>
      await api.order.bulkConfirm({ public_ids: publicIds }),
    onSuccess: (res) => {
      const result = res.data;
      setBulkResult(result);
      if (result.confirmed_count > 0) {
        toast.success(`${result.confirmed_count} 件を確定しました`);
      }
      const failedIds = new Set(result.failed.map((f) => f.public_id));
      setRowSelection((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([id]) => failedIds.has(id))),
      );
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (err: unknown) => {
      // 通信・サーバエラーは選択を保持したままにして、そのまま再実行できるようにする。
      toast.error(`一括確定に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  // 取り消せない一括操作なので、件数を見せてから実行する。
  const handleBulkConfirm = async () => {
    if (bulkConfirm.isPending || overLimit || selectedIds.length === 0) return;
    const ok = await confirm({
      title: `${selectedIds.length}件の受注を確定します。`,
      confirmLabel: "確定する",
    });
    if (ok) bulkConfirm.mutate(selectedIds);
  };

  const handleDateSelect = (d: string) => {
    // 日付を跨いだ受注をまとめて確定してしまわないよう、日付切替で選択は捨てる。
    clearSelection();
    navigate({ search: (prev) => ({ ...prev, delivery_date: d, page: 1 }) });
  };

  // #754: 片側だけの指定時刻 (開始 or 終了が空) は最適化で便区分窓の端まで広げられ、
  // 指定から数時間ずれた配達が組まれる。取込時の補完 (#657) が入る前に取り込まれた受注は
  // この状態のまま残るので、受注一覧から取込と同じ規則で埋め直せるようにする。
  //
  // **出す件数は API が実際に更新する集合と一致させる。**
  // - 件数は `filtered` (キーワード検索後) ではなく取得結果そのものを数える。補完はその日の
  //   受注をまとめて対象にするので、検索語を打つたびに件数が減ると実行結果と食い違う。
  // - 同じ理由でバナーは「すべて」タブにだけ出す。ステータスで絞った一覧の件数はその日の
  //   一部でしかなく、「1 件あります」と出しておいて 5 件補完される、になる。
  // - 内容編集がロックされたステータス (配車済・完了・キャンセル) と、許容幅では埋められない
  //   受注は API 側も更新しないので「補完できる件数」から外す。数えるだけ数えて押しても
  //   減らないと、運用者に押し続けさせてしまう。
  //
  // ただし**配車済みの片側指定は別に数えて出す**。自動配車を確定すると受注は assigned に
  // なるため、この状況では補完できる件数が 0 になる。そこでバナーごと消すと、最適化の警告が
  // 「受注一覧から一括補完してください」と案内しているのに一覧には何も出ない行き止まりになる。
  // 完了・キャンセルは含めない (過去日を開くたびに消せないバナーが出続けるうえ、
  // 「配車から外す」という次の一手がそもそも取れない)。
  const tolerance = useImportTimeTolerance();
  const partialWindow = useMemo(() => {
    const empty = { fillableCount: 0, unfillableCount: 0, assignedCount: 0 };
    // 許容幅を取れるまでは件数を出さない (誤った件数を約束しない)。
    if (status !== "all" || !tolerance) return empty;
    const rows = (data?.data ?? []).filter(orderHasPartialTimeWindow);
    const editable = rows.filter((o) => !isOrderContentLocked(o.status));
    const fillableCount = editable.filter((o) => canBackfillOrderTimeWindow(o, tolerance)).length;
    return {
      fillableCount,
      unfillableCount: editable.length - fillableCount,
      assignedCount: rows.filter((o) => o.status === "assigned").length,
    };
  }, [data, status, tolerance]);
  const fillableCount = partialWindow.fillableCount;
  const showBackfillBanner =
    fillableCount > 0 || partialWindow.unfillableCount > 0 || partialWindow.assignedCount > 0;

  const backfillTimeWindows = useMutation({
    mutationFn: async () => await api.order.backfillPartialTimeWindows({ delivery_date }),
    onSuccess: (res) => {
      const result = res.data;
      if (result.updated_count > 0) {
        toast.success(`${result.updated_count} 件の指定時間を補完しました`);
      } else {
        toast.info("補完できる受注はありませんでした");
      }
      // 上限で残した分は「押し直せば続く」ことまで言わないと、途中で止まったように見える。
      if (result.remaining_count > 0) {
        toast.warning(
          `残り ${result.remaining_count} 件は 1 回あたりの上限 (${BACKFILL_PARTIAL_TIME_WINDOWS_MAX} 件) を超えたため未処理です。もう一度実行すると続きから補完します`,
        );
      }
      // 「配車から外せば直る」件数と「終わった記録なので直さない」件数を混ぜない。
      // 完了・キャンセルまで「レーンから取り外してください」と言うと取れない手を案内することになる。
      if (result.skipped_locked_count > 0) {
        toast.warning(
          `${result.skipped_locked_count} 件は配車済み / 確定済みレーンに割当済みのため補完できませんでした。配車画面でレーンから取り外すと補完できます`,
        );
      }
      if (result.skipped_finalized_count > 0) {
        toast.info(
          `${result.skipped_finalized_count} 件は完了・キャンセル済みのため補完していません`,
        );
      }
      if (result.scan_truncated) {
        toast.warning(
          `この日の受注が走査上限 (${BACKFILL_PARTIAL_TIME_WINDOWS_SCAN_MAX} 件) に達したため、一部を見ていません`,
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (err: unknown) => {
      toast.error(`一括補完に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  return (
    <div
      style={{
        margin: -24,
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 48px)",
        overflow: "hidden",
      }}
    >
      <PageHeader>
        <Button
          render={
            <Link to="/orders/new" search={orderListSearch} style={{ textDecoration: "none" }} />
          }
          variant="outline"
          size="sm"
        >
          + 新規作成
        </Button>
        <Button variant="default" size="sm" onClick={() => setImportOpen(true)}>
          注文書一括取込
        </Button>
      </PageHeader>

      {/* #641: 境界を取得できないと便区分を出せない。理由と再試行を出し、一覧自体は表示する。 */}
      {boundariesError && (
        <QueryErrorBanner
          label="便区分の設定"
          error={boundariesError}
          onRetry={refetchBoundaries}
        />
      )}

      <WeekStrip
        days={weekDays}
        selected={delivery_date}
        onSelect={handleDateSelect}
        onPrev={() => handleDateSelect(shiftDate(delivery_date, -7))}
        onNext={() => handleDateSelect(shiftDate(delivery_date, 7))}
        onToday={() => handleDateSelect(today)}
        title={monthLabel}
        onPrevMonth={() => handleDateSelect(shiftMonth(delivery_date, -1))}
        onNextMonth={() => handleDateSelect(shiftMonth(delivery_date, 1))}
      />

      <div
        style={{
          display: "flex",
          gap: 0,
          borderBottom: "0.5px solid var(--rule)",
          padding: "0 32px",
          background: "var(--bg-panel)",
        }}
      >
        {STATUS_TABS.map((t) => {
          const active = status === t.v;
          return (
            <button
              key={t.v}
              type="button"
              onClick={() => handleTabChange(t.v)}
              style={{
                padding: "10px 16px",
                fontSize: 12,
                color: active ? "var(--ink)" : "var(--ink-3)",
                fontWeight: active ? 500 : 400,
                borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
                marginBottom: -1,
              }}
            >
              {t.label}
              <span
                style={{
                  marginLeft: 6,
                  fontFamily: "var(--f-mono)",
                  fontSize: 10,
                  color: "var(--ink-4)",
                }}
              >
                {counts[t.v]}
              </span>
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          // #754: 補完バナーが入る日は幅が足りなくなりうる。折り返して「表示: N / M」を押し出さない。
          flexWrap: "wrap",
          gap: 12,
          padding: "12px 32px",
          borderBottom: "0.5px solid var(--rule-soft)",
          background: "var(--bg)",
        }}
      >
        <SearchInput
          value={localQ}
          onChange={handleQChange}
          placeholder="注文番号・配送先・取引先で検索"
          pending={debouncer.state.isPending}
          width={280}
          ariaLabel="受注を検索"
        />
        {showBackfillBanner && (
          <div
            role="status"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 12px",
              border: "0.5px solid #d6a13a",
              background: "rgba(214, 161, 58, 0.1)",
              color: "#a35a00",
              fontSize: 11,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span>
                指定時間が片側だけの受注があります (最適化で便区分の時間帯の端まで広げられます)
              </span>
              {fillableCount > 0 && (
                <span>
                  取込と同じ許容幅で補完できる受注: {fillableCount} 件
                  {fillableCount > BACKFILL_PARTIAL_TIME_WINDOWS_MAX &&
                    ` (1 回の実行で最大 ${BACKFILL_PARTIAL_TIME_WINDOWS_MAX} 件。残りはもう一度実行してください)`}
                </span>
              )}
              {/* 押しても減らない件数を黙って混ぜず、次の一手まで書く (行き止まりを作らない)。 */}
              {partialWindow.assignedCount > 0 && (
                <span>
                  配車済みのため補完できない受注: {partialWindow.assignedCount} 件
                  (配車画面でレーンから取り外すと補完できます)
                </span>
              )}
              {partialWindow.unfillableCount > 0 && (
                <span>
                  許容幅では補完できない受注: {partialWindow.unfillableCount} 件
                  (受注詳細で開始・終了を直接入力してください。取込許容幅が 0
                  分ならシステム設定でも直せます)
                </span>
              )}
            </div>
            {fillableCount > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={backfillTimeWindows.isPending}
                onClick={() => backfillTimeWindows.mutate()}
              >
                {backfillTimeWindows.isPending ? "補完中…" : "この日をまとめて補完"}
              </Button>
            )}
          </div>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
          表示: {filtered.length} / {counts.all}
        </span>
      </div>

      <div style={{ flex: 1, minHeight: 0, padding: "0 32px 80px", overflow: "auto" }}>
        {error && (
          <p style={{ color: "var(--danger)", padding: "24px 0", fontSize: 12 }}>
            エラー: {String(error)}
          </p>
        )}
        <table className="tbl" style={{ marginTop: 4 }}>
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => {
                  const canSort = h.column.getCanSort();
                  const sortDir = h.column.getIsSorted();
                  const headerNode = h.isPlaceholder
                    ? null
                    : flexRender(h.column.columnDef.header, h.getContext());
                  return (
                    <th
                      key={h.id}
                      aria-sort={
                        !canSort
                          ? undefined
                          : sortDir === "asc"
                            ? "ascending"
                            : sortDir === "desc"
                              ? "descending"
                              : "none"
                      }
                      style={{ width: h.getSize(), position: "relative" }}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={h.column.getToggleSortingHandler()}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            background: "transparent",
                            border: 0,
                            padding: 0,
                            cursor: "pointer",
                            color: "inherit",
                            font: "inherit",
                            textAlign: "left",
                          }}
                        >
                          {headerNode}
                          {sortDir === "asc" ? (
                            <ChevronUp
                              size={12}
                              aria-hidden="true"
                              style={{ color: "var(--ink-3)", flexShrink: 0 }}
                            />
                          ) : sortDir === "desc" ? (
                            <ChevronDown
                              size={12}
                              aria-hidden="true"
                              style={{ color: "var(--ink-3)", flexShrink: 0 }}
                            />
                          ) : (
                            <ChevronsUpDown
                              size={12}
                              aria-hidden="true"
                              style={{ color: "var(--ink-4)", flexShrink: 0 }}
                            />
                          )}
                        </button>
                      ) : (
                        headerNode
                      )}
                      {h.column.getCanResize() && (
                        <button
                          type="button"
                          aria-label={`${h.column.id} 列幅をリサイズ`}
                          onMouseDown={h.getResizeHandler()}
                          onTouchStart={h.getResizeHandler()}
                          onDoubleClick={() => h.column.resetSize()}
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            height: "100%",
                            width: 6,
                            cursor: "col-resize",
                            background: h.column.getIsResizing() ? "var(--accent)" : "transparent",
                            border: 0,
                            padding: 0,
                            userSelect: "none",
                            touchAction: "none",
                          }}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {showSkeleton && <TableSkeletonRows rows={8} columns={columns.length} />}
            {!showSkeleton && table.getRowModel().rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ padding: 32, textAlign: "center", color: "var(--ink-3)", fontSize: 12 }}
                >
                  該当する受注がありません。
                </td>
              </tr>
            )}
            {!showSkeleton &&
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() =>
                    navigate({
                      to: "/orders/$publicId",
                      params: { publicId: row.original.id },
                      search: orderListSearch,
                    })
                  }
                  style={{ cursor: "pointer" }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      // 選択チェックボックスの操作で詳細画面へ遷移させない。
                      onClick={cell.column.id === "select" ? (e) => e.stopPropagation() : undefined}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* #689: 確定できなかった受注は件数だけでなく注文番号と理由まで出す (直す先が分かるように)。 */}
      {bulkResult && bulkResult.failed_count > 0 && (
        <div
          role="alert"
          style={{
            margin: "0 32px 8px",
            padding: "10px 12px",
            border: "0.5px solid var(--rule)",
            borderLeft: "3px solid var(--danger)",
            borderRadius: 4,
            background: "var(--bg-panel)",
            fontSize: 11,
            color: "var(--ink-2)",
            maxHeight: 180,
            overflow: "auto",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <strong style={{ color: "var(--ink)" }}>
              {bulkResult.confirmed_count > 0 && `${bulkResult.confirmed_count} 件を確定しました。`}
              {`${bulkResult.failed_count} 件は確定できませんでした。`}
            </strong>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              aria-label="確定結果を閉じる"
              onClick={() => setBulkResult(null)}
              style={{ display: "flex", color: "var(--ink-3)" }}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
          <ul style={{ margin: "6px 0 0", paddingLeft: 16, listStyle: "disc" }}>
            {bulkResult.failed.map((f) => (
              <li key={f.public_id} style={{ marginTop: 2 }}>
                {/* 直しに行けるよう注文番号から受注詳細へ飛ばす。一覧の条件は search で引き継ぐ (#673)。
                    存在しない受注 (not_found) は開いても 404 なのでリンクにしない。 */}
                {f.reason_code === "not_found" ? (
                  <span style={{ fontFamily: "var(--f-mono)" }}>
                    {f.order_number ?? f.public_id}
                  </span>
                ) : (
                  <Link
                    to="/orders/$publicId"
                    params={{ publicId: f.public_id }}
                    search={orderListSearch}
                    style={{ fontFamily: "var(--f-mono)", color: "var(--accent)" }}
                  >
                    {f.order_number ?? f.public_id}
                  </Link>
                )}
                {` — ${f.reason}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {selectedIds.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            margin: "0 32px 8px",
            padding: "8px 12px",
            border: "0.5px solid var(--accent)",
            borderRadius: 4,
            background: "color-mix(in oklch, var(--accent) 8%, var(--bg-panel))",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--ink)" }}>
            {`${selectedIds.length} 件選択中`}
          </span>
          {overLimit && (
            <span style={{ fontSize: 11, color: "var(--danger)" }}>
              {`一度に確定できるのは ${BULK_CONFIRM_ORDERS_MAX} 件までです`}
            </span>
          )}
          <span style={{ flex: 1 }} />
          <Button variant="ghost" size="sm" onClick={clearSelection}>
            選択解除
          </Button>
          <Button
            variant="default"
            size="sm"
            disabled={bulkConfirm.isPending || overLimit}
            onClick={() => void handleBulkConfirm()}
          >
            {bulkConfirm.isPending ? "確定中…" : `${selectedIds.length} 件を確定する`}
          </Button>
        </div>
      )}

      <TablePagination table={table} />

      <BulkImportModal open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}

function PageHeader({ children }: { children?: React.ReactNode }) {
  return (
    <div
      className="cp-pageheader"
      style={{
        padding: "12px 32px",
        display: "flex",
        alignItems: "center",
        gap: 24,
        borderBottom: "0.5px solid var(--rule-softer)",
        background: "var(--bg)",
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>{children}</div>
    </div>
  );
}

function StatusChip({ status }: { status: OrderDto["status"] }) {
  const { bg, ink } = STATUS_CHIP_COLOR[status];
  const label = ORDER_STATUS_LABELS[status];
  return (
    <span
      className="chip"
      style={{
        background: bg,
        color: ink,
        border: 0,
      }}
    >
      <span className="dot" />
      {label}
    </span>
  );
}

const COL_SIZE_STORAGE_KEY = "cp:orders:colSizes";

function loadColumnSizing(): ColumnSizingState {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(COL_SIZE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object") return parsed as ColumnSizingState;
  } catch {
    // ignore
  }
  return {};
}

function parseSortParam(raw: string): SortingState {
  if (!raw) return [];
  return raw
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => {
      const desc = token.startsWith("-");
      const id = desc ? token.slice(1) : token;
      return { id, desc };
    });
}

function stringifySortParam(sort: SortingState): string {
  return sort.map((s) => (s.desc ? `-${s.id}` : s.id)).join(",");
}

function formatDeliveryDate(yyyymmdd: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(yyyymmdd)) return yyyymmdd;
  const [_y, m, d] = yyyymmdd.split("-");
  const dt = new Date(`${yyyymmdd}T00:00:00`);
  const dow = ["日", "月", "火", "水", "木", "金", "土"][dt.getDay()] ?? "";
  return `${m}/${d}(${dow})`;
}
